import { useRef, useState, useEffect } from 'react';
import { FiCheck, FiShield } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { property } from '../config/property';
import { createPayload, getSuccessMessage, resetSubmission, sendLead, validateLead } from '../lib/lead';
import { trackEvent, trackLeadConversion } from '../lib/tracking';

const initialLead = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  interest: 'In-person showing',
  timeline: '',
  workingWithAgent: '',
  message: '',
  website: '',
  contactPreference: '',
  availability: '',
};

export default function LeadForm() {
  const [lead, setLead] = useState(initialLead);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [submitError, setSubmitError] = useState('');
  const [started, setStarted] = useState(false);
  const submitting = useRef(false);
  const formRef = useRef(null);

  useEffect(() => {
    // Clear the old "accepted" flag so a reload always shows the form.
    try { sessionStorage.removeItem('azalea-accepted-at'); } catch { /* Nothing stored. */ }
    const selectInterest = (event) => {
      setLead((current) => ({ ...current, interest: typeof event.detail === 'string' ? event.detail : 'In-person showing' }));
      setStatus((current) => (current === 'success' ? 'idle' : current));
      // Move focus to the form for keyboard and screen-reader users without opening the mobile keyboard.
      window.setTimeout(() => formRef.current?.focus({ preventScroll: true }), 60);
    };
    window.addEventListener('property-interest', selectInterest);
    return () => window.removeEventListener('property-interest', selectInterest);
  }, []);

  useEffect(() => {
    if (status === 'success') document.getElementById('inquiry-form')?.scrollIntoView({ block: 'center' });
  }, [status]);

  const update = (event) => {
    const { name } = event.target;
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setLead((current) => ({ ...current, [name]: value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: '' }));
    if (!started && name !== 'website') {
      setStarted(true);
      try {
        if (!sessionStorage.getItem('azalea-form-started')) {
          sessionStorage.setItem('azalea-form-started', '1');
          trackEvent('LeadFormStarted', { content_name: property.address });
        }
      } catch {
        trackEvent('LeadFormStarted', { content_name: property.address });
      }
    }
  };

  const startNewRequest = () => {
    resetSubmission();
    setLead(initialLead);
    setErrors({});
    setSubmitError('');
    setStarted(false);
    setStatus('idle');
  };

  const submit = async (event) => {
    event.preventDefault();
    if (status === 'sending' || submitting.current) return;
    if (lead.website) {
      console.warn('Honeypot triggered');
      return;
    }
    const nextErrors = validateLead(lead);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      const field = document.querySelector(`[name="${Object.keys(nextErrors)[0]}"]`);
      field?.focus();
      field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    submitting.current = true;
    setSubmitError('');
    setStatus('sending');
    try {
      const payload = await createPayload(lead);
      const result = await sendLead(payload);
      setStatus('success');
      // Meta Lead event: only after the webhook confirmed the lead, using the same ID that was sent to it.
      if (result.confirmed) {
        try {
          trackLeadConversion({ eventId: payload.event_id, value: property.price, propertyAddress: property.address, payload });
        } catch (trackingError) {
          console.warn('Meta Lead tracking unavailable', trackingError);
        }
      }
      // The next request gets its own event ID.
      resetSubmission();
    } catch (error) {
      console.error('Lead submission failed:', error);
      setSubmitError(error?.message || '');
      setStatus('error');
    } finally {
      submitting.current = false;
    }
  };

  const buttonLabel = lead.interest === 'Property question' ? 'Send My Question' : 'Request a Showing';
  if (status === 'success') {
    const result = getSuccessMessage(lead.interest);
    return (
      <div className="success-card" id="inquiry-form" role="status" aria-live="polite">
        <span className="success-mark"><SafeIcon icon={FiCheck} /></span>
        <p className="eyebrow">Request received</p>
        <h3>{result.title}</h3>
        <p>{result.message}</p>
        <div className="success-contact">
          {property.contactPhone && <a href={`tel:${property.contactPhone}`}>Call</a>}
          {property.contactEmail && <a href={`mailto:${property.contactEmail}`}>Email</a>}
          <button type="button" onClick={startNewRequest}>Send another request</button>
        </div>
      </div>
    );
  }

  return (
    <form className="lead-form" id="inquiry-form" ref={formRef} tabIndex={-1} onSubmit={submit} aria-busy={status === 'sending'} noValidate>
      <div className="form-grid">
        <SelectField label="Preferred contact method" name="contactPreference" value={lead.contactPreference} onChange={update} error={errors.contactPreference} options={['Call', 'Email', 'Text']} />
        <SelectField label="Desired next step" name="interest" value={lead.interest} onChange={update} error={errors.interest} options={['In-person showing', 'Property question']} />
        <Field label="First name" name="firstName" value={lead.firstName} onChange={update} error={errors.firstName} autoComplete="given-name" />
        <Field label="Last name" name="lastName" value={lead.lastName} onChange={update} error={errors.lastName} autoComplete="family-name" />
        {lead.contactPreference === 'Email' && (
          <Field label="Email address" name="email" type="email" value={lead.email} onChange={update} error={errors.email} autoComplete="email" />
        )}
        {['Call', 'Text'].includes(lead.contactPreference) && (
          <Field label="Phone number" name="phone" type="tel" value={lead.phone} onChange={update} error={errors.phone} autoComplete="tel" />
        )}
        {!lead.contactPreference && <p className="lf-hint" style={{ gridColumn: '1 / -1', margin: 0 }}>Choose a contact method and we will ask for just that one contact detail.</p>}
      </div>
      <p className="privacy-note">Provide the contact information needed for your preferred reply method. Represented buyers are welcome; Fanny can coordinate with your agent.</p>
      <label className="field message-field">
        <span>Message or questions <small>Optional</small></span>
        <textarea name="message" value={lead.message} onChange={update} rows="4" maxLength="2000" placeholder="Anything you'd like to know?" />
      </label>
      <details className="lf-more">
        <summary>Add optional details</summary>
        <div className="lf-more-body">
          <SelectField label="Purchase timeline" name="timeline" value={lead.timeline} onChange={update} options={['Within 30 days', 'One to three months', 'More than three months', 'Exploring']} />
          <Field label="Showing availability (optional)" name="availability" value={lead.availability} onChange={update} />
          <SelectField label="Working with a real estate agent? (optional)" name="workingWithAgent" value={lead.workingWithAgent} onChange={update} options={['Yes', 'No']} />
        </div>
      </details>
      <label className="honeypot" aria-hidden="true">
        Website
        <input name="website" value={lead.website} onChange={update} tabIndex="-1" autoComplete="off" />
      </label>
      <div className="inquiry-consents">
        <a href="#privacy-policy" onClick={(event) => { event.preventDefault(); document.getElementById('privacy-policy')?.click(); }}>Privacy information</a>
      </div>
      <span className="sr-only" role="status" aria-live="polite">{status === 'sending' ? 'Sending your request' : ''}</span>
      {status === 'error' && <p className="form-error form-submit-error" role="alert">We couldn't confirm receipt. Please try again, or call Fanny at (980) 202-3043 or email {property.contactEmail}.{submitError ? ` (${submitError})` : ''}</p>}
      <button className="button button-dark submit-button" type="submit" disabled={status === 'sending'}>
        {status === 'sending' ? 'Sending...' : buttonLabel}
      </button>
      <p className="privacy-note"><SafeIcon icon={FiShield} /> Your information will be used to respond to your property inquiry.</p>
    </form>
  );
}
function Field({ label, name, value, onChange, error, type = 'text', autoComplete }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input type={type} name={name} value={value} onChange={onChange} autoComplete={autoComplete} aria-invalid={Boolean(error)} aria-describedby={error ? `${name}-error` : undefined} />
      {error && <small className="field-error" id={`${name}-error`}>{error}</small>}
    </label>
  );
}

function SelectField({ label, name, value, onChange, error, options }) {
  return (
    <label className="field">
      <span>{label}</span>
      <select name={name} value={value} onChange={onChange} aria-invalid={Boolean(error)} aria-describedby={error ? `${name}-error` : undefined}>
        <option value="">Choose one</option>
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
      {error && <small className="field-error" id={`${name}-error`}>{error}</small>}
    </label>
  );
}